/**
 * Conflict resolution for cross-device sync. Pure — no fetch, no
 * localStorage, no store imports — so the rules that decide whether an answer
 * survives a merge can be unit-tested exhaustively. The stateful half lives in
 * `lib/sync.ts`, the same way `srs.ts` is pure and `hooks/useSrs.ts` is not.
 *
 * Only two stores sync. `mastery` is a pure function of `srs` (see
 * `deriveMastery` in `study.ts`), and `settings` / `theme` are deliberately
 * per-device.
 *
 * Two clocks appear here and they must not be confused:
 *
 *   `rev`        server-assigned, monotonic, only ever a pull cursor
 *   `updated_at` the writing device's wall clock, only ever a tiebreak
 *
 * Using `updated_at` as a cursor would let a device with a skewed clock skip
 * records permanently; using `rev` as a tiebreak would make whoever pushed
 * last win regardless of who edited last.
 */

export type SyncKind = "srs" | "activity";

export interface SyncRecord {
  kind: SyncKind;
  key: string;
  /** JSON-encoded value. `null` is a tombstone — the record was deleted. */
  value: string | null;
  updated_at: number;
  /** Server-assigned. Absent on records a client is about to push. */
  rev?: number;
}

/**
 * What we know about one record's sync state. Kept in a sidecar map rather
 * than inside the record itself because a tombstone still needs a timestamp
 * after its value is gone — `mark(day, index, "untouched")` is an ordinary
 * user action that deletes the record, and an embedded timestamp would die
 * with it.
 */
export interface RecordMeta {
  /** `updated_at` of the last local write, or of the last accepted remote one. */
  at: number;
  dirty: 0 | 1;
}

/** Keyed by `metaKey(kind, key)` — e.g. `"srs/3:12"`. */
export type MetaMap = Record<string, RecordMeta>;

export function metaKey(kind: SyncKind, key: string): string {
  return `${kind}/${key}`;
}

/**
 * Build the push payload from whatever is currently flagged dirty.
 *
 * `read` returns the live value for a key, or `undefined` when the record no
 * longer exists locally. That absence is the whole tombstone mechanism: a key
 * stamped dirty with nothing behind it is emitted as `value: null`, which is
 * how a delete travels to the other devices.
 */
export function collectChanges(
  meta: MetaMap,
  read: (kind: SyncKind, key: string) => unknown
): SyncRecord[] {
  const out: SyncRecord[] = [];
  for (const [mk, entry] of Object.entries(meta)) {
    if (!entry.dirty) continue;
    const sep = mk.indexOf("/");
    if (sep < 0) continue;
    const kind = mk.slice(0, sep) as SyncKind;
    const key = mk.slice(sep + 1);
    const value = read(kind, key);
    out.push({
      kind,
      key,
      value: value === undefined ? null : JSON.stringify(value),
      updated_at: entry.at,
    });
  }
  return out;
}

export interface MergeResult<T> {
  next: Record<string, T>;
  nextMeta: MetaMap;
  /** How many records actually changed — lets a caller skip a re-render. */
  changed: number;
}

/**
 * Apply a batch of incoming records of one kind, last-write-wins per record.
 *
 * A record with no local metadata always yields to the server. That branch is
 * load-bearing: rows produced locally by `planSeed` are derived state that was
 * never pushed, and they must not beat a real graded answer from another
 * device just because they happen to be newer.
 */
export function mergeDelta<T>(
  local: Readonly<Record<string, T>>,
  meta: MetaMap,
  kind: SyncKind,
  incoming: readonly SyncRecord[]
): MergeResult<T> {
  const next: Record<string, T> = { ...local };
  const nextMeta: MetaMap = { ...meta };
  let changed = 0;

  for (const rec of incoming) {
    if (rec.kind !== kind) continue;
    const mk = metaKey(kind, rec.key);
    const known = nextMeta[mk];
    // `<=` keeps the incumbent on an exact tie, so re-applying a record we
    // already have is a no-op rather than a pointless write.
    if (known != null && rec.updated_at <= known.at) continue;

    if (rec.value === null) {
      delete next[rec.key];
    } else {
      next[rec.key] = JSON.parse(rec.value) as T;
    }
    nextMeta[mk] = { at: rec.updated_at, dirty: 0 };
    changed += 1;
  }

  return { next, nextMeta, changed };
}

/**
 * Clear dirty flags after a push, but only for keys the user hasn't touched
 * since the request went out.
 *
 * `sent` maps each pushed key to the `at` it carried. A push can be in flight
 * for a second while the learner answers three more cards; clearing the flag
 * set we captured at send time would drop those answers silently.
 *
 * Only ever clears — never marks anything dirty — so it is safe to run in
 * either order relative to `mergeDelta` on the echoed rows.
 */
export function reconcilePush(meta: MetaMap, sent: Readonly<Record<string, number>>): MetaMap {
  const next: MetaMap = { ...meta };
  for (const [mk, at] of Object.entries(sent)) {
    const entry = next[mk];
    if (entry != null && entry.dirty && entry.at === at) {
      next[mk] = { at: entry.at, dirty: 0 };
    }
  }
  return next;
}

/**
 * Advance the pull cursor. Taken from the records themselves rather than from
 * any server-side counter — a `rev` read separately from its rows could be
 * observed before those rows commit, which would strand them forever.
 */
export function nextCursor(prev: number, records: readonly SyncRecord[]): number {
  let max = prev;
  for (const rec of records) {
    if (rec.rev != null && rec.rev > max) max = rec.rev;
  }
  return max;
}

/**
 * Flag keys for the next push. Used for a local edit, for adopting local state
 * into a fresh account, for an import, and for a reset — a reset is just this
 * over every known key, with the values removed underneath (see
 * `collectChanges`).
 */
export function stampDirty(
  meta: MetaMap,
  keys: readonly string[],
  now: number
): MetaMap {
  const next: MetaMap = { ...meta };
  for (const mk of keys) {
    next[mk] = { at: now, dirty: 1 };
  }
  return next;
}

/**
 * A wall clock that never goes backwards for this device.
 *
 * An NTP correction or a manual time change that moves the clock back would
 * otherwise make every later edit to an already-synced record lose the
 * last-write-wins comparison forever — the record would be frozen at its
 * remote value with no way to overwrite it.
 */
export function monotonicNow(lastAt: number, wallClock: number): number {
  return wallClock > lastAt ? wallClock : lastAt + 1;
}

/**
 * Split a push into request-sized batches. D1 caps bound parameters per
 * statement, and the big pushes here are real: a reset tombstones every record
 * the learner has, and adopting local state into a new account pushes ~1200.
 */
export function chunk<T>(items: readonly T[], size: number): T[][] {
  if (size <= 0) return [items.slice()];
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
}

/* ------------------------------------------------------------ first sign-in */

/** What the learner picks when both this device and the account have progress. */
export type FirstSyncStrategy = "merge" | "use-account";

/** Numbers for the confirmation dialog, computed by a dry-run merge. */
export interface FirstSyncPreview {
  localWords: number;
  remoteWords: number;
  both: number;
  newerHere: number;
  newerThere: number;
}

export interface FirstSyncMerge<S, A> {
  srs: Record<string, S>;
  activity: Record<string, A>;
  meta: MetaMap;
  preview: FirstSyncPreview;
}

/**
 * Bring a device that has progress together with an account that has progress
 * — the case the ordinary delta merge cannot handle, because local rows carry
 * no metadata the server knows about.
 *
 * Per `srs` key the more recently edited side wins, exactly the steady-state
 * rule. The local timestamp is `localAt(key)` — the `at` written by every local
 * edit even while signed out, falling back to the record's own `last`; rows
 * with neither (seeded/derived) yield to the account. Words only one side
 * knows are kept, and local-only ones are flagged for push: the account has
 * no row to lose. A newer account tombstone deletes the local row; an older
 * one is overridden by the local answer.
 *
 * `activity` keys are per device, so they union. The same key on both sides
 * (this device synced here before, then signed out) takes the larger of each
 * counter — they only ever grow.
 */
export function mergeFirstSync<S extends { last?: number }, A extends { reviews: number; learned: number }>(
  localSrs: Readonly<Record<string, S>>,
  localActivity: Readonly<Record<string, A>>,
  localMeta: MetaMap,
  records: readonly SyncRecord[],
  now: number
): FirstSyncMerge<S, A> {
  const remote = new Map<string, SyncRecord>();
  for (const rec of records) remote.set(metaKey(rec.kind, rec.key), rec);

  const localAt = (kind: SyncKind, key: string, record: S | undefined): number => {
    const m = localMeta[metaKey(kind, key)];
    if (m != null && m.at > 0) return m.at;
    return record?.last ?? 0;
  };

  const srs: Record<string, S> = {};
  const activity: Record<string, A> = {};
  const meta: MetaMap = {};
  let remoteWords = 0;
  let both = 0;
  let newerHere = 0;
  let newerThere = 0;

  const srsKeys = new Set<string>(Object.keys(localSrs));
  for (const rec of records) if (rec.kind === "srs") srsKeys.add(rec.key);
  for (const key of srsKeys) {
    const mk = metaKey("srs", key);
    const local = localSrs[key];
    const rec = remote.get(mk);
    if (rec == null) {
      srs[key] = local!;
      const at = localAt("srs", key, local);
      meta[mk] = { at: at > 0 ? at : now, dirty: 1 };
      continue;
    }
    if (rec.value !== null) remoteWords += 1;
    if (local == null) {
      if (rec.value !== null) srs[key] = JSON.parse(rec.value) as S;
      meta[mk] = { at: rec.updated_at, dirty: 0 };
      continue;
    }
    if (rec.value !== null) both += 1;
    const at = localAt("srs", key, local);
    if (at > rec.updated_at) {
      srs[key] = local;
      meta[mk] = { at, dirty: 1 };
      if (rec.value !== null) newerHere += 1;
    } else {
      if (rec.value !== null) srs[key] = JSON.parse(rec.value) as S;
      meta[mk] = { at: rec.updated_at, dirty: 0 };
      if (rec.value !== null) newerThere += 1;
    }
  }

  const activityKeys = new Set<string>(Object.keys(localActivity));
  for (const rec of records) if (rec.kind === "activity") activityKeys.add(rec.key);
  for (const key of activityKeys) {
    const mk = metaKey("activity", key);
    const local = localActivity[key];
    const rec = remote.get(mk);
    const remoteValue = rec?.value == null ? undefined : (JSON.parse(rec.value) as A);
    if (rec == null) {
      activity[key] = local!;
      meta[mk] = { at: now, dirty: 1 };
      continue;
    }
    if (local == null) {
      if (remoteValue != null) activity[key] = remoteValue;
      meta[mk] = { at: rec.updated_at, dirty: 0 };
      continue;
    }
    const merged = {
      ...local,
      reviews: Math.max(local.reviews, remoteValue?.reviews ?? 0),
      learned: Math.max(local.learned, remoteValue?.learned ?? 0),
    };
    activity[key] = merged;
    const differs =
      remoteValue == null || merged.reviews !== remoteValue.reviews || merged.learned !== remoteValue.learned;
    meta[mk] = differs
      ? { at: monotonicNow(rec.updated_at, now), dirty: 1 }
      : { at: rec.updated_at, dirty: 0 };
  }

  return {
    srs,
    activity,
    meta,
    preview: {
      localWords: Object.keys(localSrs).length,
      remoteWords,
      both,
      newerHere,
      newerThere,
    },
  };
}
